import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { postMessagesConversations } from '@/api/generated/messages-messages/messages-messages'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { useSendOnEnter, SEND_ON_ENTER_HINT } from './useSendOnEnter'
import { ApiError } from '@/api/http'

const MAX_BODY = 2000

interface ComposeMessageDialogProps {
  recipientUserId: number
  recipientUsername: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** Reusable "Message {user}" modal — drop next to any username display. */
export function ComposeMessageDialog({
  recipientUserId,
  recipientUsername,
  open,
  onOpenChange,
}: ComposeMessageDialogProps) {
  const [body, setBody] = useState('')
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()

  // The component stays mounted while closed (only Radix's DialogContent
  // portal unmounts), so state/effects here outlive a single "open" session.
  const openRef = useRef(open)
  // Sync during render (accepted ref pattern) — an effect flushes after paint,
  // leaving a window where a fast-resolving mutation could read a stale value.
  openRef.current = open
  useEffect(() => {
    if (open) {
      // Fresh session each open — the component stays mounted while closed,
      // so stale drafts/errors from an abandoned attempt would otherwise resurface.
      setBody('')
      setError(null)
    }
  }, [open])

  const sendMutation = useMutation({
    mutationFn: () => postMessagesConversations({ recipientUserId, body: body.trim() }),
    onSuccess: (res) => {
      if (!openRef.current) return // dismissed mid-flight — user cancelled; don't navigate
      onOpenChange(false)
      setBody('')
      navigate(`/messages/${res.conversationId}`)
    },
    onError: (err) => {
      if (!openRef.current) return
      // The fetcher throws ApiError with the raw JSON body attached, so pull
      // the server's `message` field straight off `err.body` rather than
      // trusting `Error.message` (that's already derived from the same
      // field, but reading `.body` is the established pattern elsewhere and
      // stays correct if ApiError's own message-derivation ever changes).
      const message =
        err instanceof ApiError
          ? ((err.body as { message?: string })?.message ?? 'Your message could not be sent.')
          : 'Your message could not be sent.'
      setError(message)
    },
  })

  const overLimit = body.length > MAX_BODY
  const canSend = body.trim().length > 0 && !overLimit && !sendMutation.isPending
  // Same composer keys as the thread: Enter sends, Shift+Enter newlines.
  const { onKeyDown: onComposerKeyDown, isDesktop: sendsOnEnter } = useSendOnEnter(
    () => sendMutation.mutate(),
    canSend,
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Message {recipientUsername}</DialogTitle>
        </DialogHeader>
        <Textarea
          aria-label="Message"
          value={body}
          onChange={(e) => { setBody(e.target.value); setError(null) }}
          onKeyDown={onComposerKeyDown}
          placeholder={`Write a message to ${recipientUsername}…`}
          rows={4}
        />
        <div className="flex items-center justify-between">
          <span className={['text-xs', overLimit ? 'text-red-600' : 'text-slate-400'].join(' ')}>
            {sendsOnEnter && <span className="mr-2 text-slate-400">{SEND_ON_ENTER_HINT}</span>}
            {body.length} / {MAX_BODY}
          </span>
          <Button size="sm" onClick={() => sendMutation.mutate()} disabled={!canSend}>
            {sendMutation.isPending ? 'Sending…' : 'Send'}
          </Button>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
      </DialogContent>
    </Dialog>
  )
}
